import React, { Dispatch, SetStateAction, useContext } from 'react';
import { Panel } from '../../../components/containers/Panel';
import { ConnectProfile } from '../../../components/socialProfiles/ConnectProfile';
import { ContainerListItem } from '../../../components/actions/ListItem/ListItem.view';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { Void } from '../../../models/basic-objects';
import {
  ConnectProfileOAuthModalType,
  ErrorModalType,
  IgModalActionEnum,
  IgModalType,
  RemoveProfileConfirmationType,
  SecondaryProfilesModalType
} from './Profiles.controller';
import {
  GetSocialProfilesQuery,
  InstagramApiType,
  Network,
  SecondaryProfileType,
  SocialOAuthContext,
  SocialOAuthProvider,
  SocialProfileFields_InstagramSocialProfile_Fragment,
  SocialProfileFieldsFragment,
  StartSocialOAuthMutationFn,
  UpdateSocialProfileDirectPublishingMutationFn
} from '../../../models/graphql';
import { AuthContextEnum } from '../../../constants/auth';
import { ISocialOAuth } from '../../../models/auth';
import { AlreadyConnectedModal } from './AlreadyConnectedModal';
import { ReconnectedAnotherModal } from './ReconnectedAnotherModal';
import { DeleteModal } from './DeleteModal';
import { Link } from 'react-router-dom';
import { SecondaryProfilesErrorModal } from './SecondaryProfilesErrorModal';
import { DirectPublishingModal } from './DirectPublishingModal';
import { profilesHelpers } from './Profiles.helpers';
import { Modal } from '../../../components/containers/Modal';
import { InfoModalObject, InfoModalView } from './InfoModal/InfoModal.view';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { SocialProfileListItem } from '../../../components/socialProfiles/SocialProfileListItem';
import { MappedProfileType } from '../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import classes from './Profiles.module.scss';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { AppContext, socialOAuthVar } from '../../../providers/AppProvider';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { ConnectSecondarySocialProfilesModal } from './ConnectSecondarySocialProfilesModal';
import { ConnectInstagramModal } from './ConnectInstagramModal';

export type SecondaryProfileModalDataType = {
  title: string;
  content: JSX.Element;
  actionText: string;
  oAuthContext: ConnectProfileOAuthModalType;
  setErrorModal: Dispatch<SetStateAction<ErrorModalType | null>>;
};

export type ProfilesViewProps = {
  //
  cancelConnect: Void;
  connectLinkedinItems: ContainerListItem[];
  directPublishingModal: SocialProfileFields_InstagramSocialProfile_Fragment | null;
  errorModal: ErrorModalType | null;
  getListActionItems: (profile: SocialProfileFieldsFragment) => ContainerListItem[];
  getProfilesData: GetSocialProfilesQuery | undefined;
  infoModal: InfoModalObject | null;
  loading: boolean;
  reconnectProfile: (
    profile: SocialProfileFieldsFragment,
    oAuthProvider: SocialOAuthProvider,
    network: Network
  ) => void;
  reconnectProfileAfterOAuth: (socialOAuth: ISocialOAuth) => Promise<void>;
  removeProfileModal: RemoveProfileConfirmationType;
  secondaryProfilesModal: SecondaryProfilesModalType | null;
  setErrorModal: Dispatch<SetStateAction<ErrorModalType | null>>;
  setDirectPublishing: (status: boolean, profile: SocialProfileFields_InstagramSocialProfile_Fragment) => void;
  setDirectPublishingModal: Dispatch<SetStateAction<SocialProfileFields_InstagramSocialProfile_Fragment | null>>;
  setInfoModal: Dispatch<SetStateAction<InfoModalObject | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setRemoveProfileModal: Dispatch<SetStateAction<RemoveProfileConfirmationType>>;
  setSecondaryProfilesModal: Dispatch<SetStateAction<SecondaryProfilesModalType | null>>;
  startSocialOAuth: StartSocialOAuthMutationFn;
  updateDirectPublishing: UpdateSocialProfileDirectPublishingMutationFn;
  hasMobileAppConnected: boolean;
  showIgConnectModal: IgModalType | undefined;
  setShowIgConnectModal: Dispatch<SetStateAction<IgModalType | undefined>>;
};

export const ProfilesView = (props: ProfilesViewProps): JSX.Element => {
  let errorModal;
  let infoModal;
  let secondaryProfileModal;
  let directPublishingModal;
  let confirmDeleteProfileModal;
  let hasConnectedIgProfiles = false;
  let disconnectedProfiles: JSX.Element[] = [];
  let connectedProfiles: JSX.Element[] = [];
  const { beacon } = useContext(BeaconContext);

  const { setLimitReachedModalState } = useContext(AppContext);
  const { canAddSocialProfile } = useOrganizationPlan();

  const getFilteredProfiles = (connected: boolean, profiles: SocialProfileFieldsFragment[]): JSX.Element[] =>
    profiles
      .filter((profile: MappedProfileType) => profile.connected === connected)
      .sort(profilesHelpers.sortProfiles)
      .map(profile => {
        const listItems = props.getListActionItems(profile);
        const network = profilesHelpers.mapNetworkFromTypename(profile.__typename);
        const provider = profilesHelpers.mapOAuthFromTypename(profile.__typename);
        const reconnectNetwork: Network =
          profile.__typename === 'InstagramSocialProfile' && profile.apiType === InstagramApiType.FACEBOOK_GRAPH
            ? Network.FACEBOOK
            : network;
        const reconnectAction = !provider
          ? undefined
          : network === Network.INSTAGRAM
          ? () => props.setShowIgConnectModal({ action: IgModalActionEnum.RECONNECT, profile: profile })
          : () => props.reconnectProfile(profile, provider, reconnectNetwork);

        return (
          <SocialProfileListItem
            key={`${profile.id}${profile.__typename}`}
            profile={profile}
            {...(profile.__typename === 'InstagramSocialProfile'
              ? {
                  setDirectPublishing: props.setDirectPublishing
                }
              : {})}
            listItems={listItems}
            reconnect={reconnectAction}
          />
        );
      });

  if (props.errorModal) {
    switch (props.errorModal.type) {
      case 'alreadyConnected':
        const oAuthContext: ConnectProfileOAuthModalType = {
          oAuthProvider: props.errorModal.oAuthProvider,
          profileNetwork: props.errorModal.network,
          context: AuthContextEnum.connectProfile,
          ...(props.errorModal.profile?.__typename === 'InstagramSocialProfile' && {
            socialOAuthContext: SocialOAuthContext.RECONNECT
          })
        };
        errorModal = (
          <AlreadyConnectedModal
            content={props.errorModal.content}
            oAuthContext={oAuthContext}
            setErrorModal={props.setErrorModal}
          />
        );
        break;
      case 'reconnectedAnother':
        const oAuthReconnectContext: ConnectProfileOAuthModalType = {
          oAuthProvider: props.errorModal.oAuthProvider,
          profileNetwork: props.errorModal.network,
          context: AuthContextEnum.reconnectProfile,
          profile: (props.errorModal.profile as SocialProfileFieldsFragment) || undefined,
          ...(props.errorModal.profile?.__typename === 'InstagramSocialProfile' && {
            socialOAuthContext: SocialOAuthContext.RECONNECT
          })
        };
        errorModal = (
          <ReconnectedAnotherModal
            content={props.errorModal.content}
            oAuthContext={oAuthReconnectContext}
            setErrorModal={props.setErrorModal}
          />
        );
        break;
      case SecondaryProfileType.COMPANY:
        const modalDataLn: SecondaryProfileModalDataType = {
          title: 'No profiles found',
          content: (
            <BoxNotification type={'warning'}>
              <>
                <p className={'no-b-m'}>
                  In order to add a Company, LinkedIn requires you to grant us permission to post for you.
                </p>
                <p className={'no-b-m'}>
                  Make sure you&apos;re currently logged into LinkedIn as an admin of that Company and try again.{' '}
                </p>
                <p className={'no-b-m'}>
                  <a onClick={() => beacon('article', '725', { type: 'modal' })}>Read our tips for solving this issue</a>.
                </p>
              </>
            </BoxNotification>
          ),
          oAuthContext: {
            oAuthProvider: props.errorModal.oAuthProvider,
            profileNetwork: props.errorModal.network,
            context: AuthContextEnum.connectCompany
          },
          actionText: 'Try Again',
          setErrorModal: props.setErrorModal
        };
        errorModal = <SecondaryProfilesErrorModal {...modalDataLn} />;
        break;
      case SecondaryProfileType.PAGE:
        let modalDataPage: SecondaryProfileModalDataType;
        switch (props.errorModal.network) {
          case Network.INSTAGRAM: {
            const message = (
              <>
                Don&apos;t see your Instagram Profile above? Make sure you&apos;re currently logged into Facebook as an
                admin of that Instagram Profile, and that your Instagram profile is a Business/Creator profile connected
                to a Facebook page.
              </>
            );
            modalDataPage = {
              title: 'No profiles found',
              content: (
                <BoxNotification type={'warning'}>
                  <p className={'no-b-m'}>{message}</p>
                  <p className={'no-b-m'}>
                    <a
                      href={
                        'https://help.smarterqueue.com/article/134-why-is-my-facebook-pagegroup-missing-when-i-try-to-add-it'
                      }
                      target={'_blank'}
                      rel={'noreferrer'}>
                      Read our tips for solving this issue.
                    </a>
                  </p>
                </BoxNotification>
              ),
              oAuthContext: {
                oAuthProvider: props.errorModal.oAuthProvider,
                profileNetwork: Network.INSTAGRAM,
                context: AuthContextEnum.connectProfile,
                socialOAuthContext: SocialOAuthContext.CONNECT
              },
              actionText: 'Try Again',
              setErrorModal: props.setErrorModal
            };
            break;
          }
          case Network.FACEBOOK: {
            const message = (
              <>
                Make sure you&apos;re currently logged into Facebook as admin of that page, and have granted ALL
                permissions.
              </>
            );
            modalDataPage = {
              title: 'No profiles found',
              content: (
                <BoxNotification type={'warning'}>
                  <p className={'no-b-m'}>{message}</p>
                  <p className={'no-b-m'}>
                    <a
                      href={
                        'https://help.smarterqueue.com/article/134-why-is-my-facebook-pagegroup-missing-when-i-try-to-add-it'
                      }
                      target={'_blank'}
                      rel={'noreferrer'}>
                      Read our tips for solving this issue.
                    </a>
                  </p>
                </BoxNotification>
              ),
              oAuthContext: {
                oAuthProvider: props.errorModal.oAuthProvider,
                profileNetwork: props.errorModal.network,
                context: AuthContextEnum.connectPage,
                socialOAuthContext: SocialOAuthContext.CONNECT
              },
              actionText: 'Try Again',
              setErrorModal: props.setErrorModal
            };
            break;
          }
          default: {
            modalDataPage = {
              title: 'No profiles found',
              content: (
                <BoxNotification type={'warning'}>
                  <p className={'no-b-m'}>
                    In order to add a Business profile, Google requires you to grant us permission to post for you.
                    Also, ensure you're logged into Google as the admin of that business..
                  </p>
                  <p className={'no-b-m'}>
                    <a onClick={() => beacon('article', '728', { type: 'modal' })}>Learn more about adding Google Business Profiles</a>
                  </p>
                </BoxNotification>
              ),
              oAuthContext: {
                oAuthProvider: props.errorModal.oAuthProvider,
                profileNetwork: props.errorModal.network,
                context: AuthContextEnum.connectPage,
                socialOAuthContext: SocialOAuthContext.CONNECT
              },
              actionText: 'Try Again',
              setErrorModal: props.setErrorModal
            };
            break;
          }
        }
        errorModal = <SecondaryProfilesErrorModal {...modalDataPage} />;
        break;
      case SecondaryProfileType.GROUP:
        const modalDataGroup: SecondaryProfileModalDataType = {
          title: 'No profiles found',
          content: (
            <BoxNotification type={'warning'}>
              <p className={'no-b-m'}>Why?</p>
              <p className={'no-b-m'}>
                In order to add a Group, Facebook required you to grant us permission to post for you. Here’s a{' '}
                <a>quick guide</a> on how to do that.
              </p>
              <p className={'no-b-m'}>
                Also, make sure you’re currently logged into Facebook as admin of that group. <a>Read our tips</a> for
                solving this issue.
              </p>
            </BoxNotification>
          ),
          oAuthContext: {
            oAuthProvider: (props.errorModal as ErrorModalType).oAuthProvider,
            profileNetwork: props.errorModal.network,
            context: AuthContextEnum.connectGroup,
            socialOAuthContext: SocialOAuthContext.CONNECT
          },
          actionText: 'Try Again',
          setErrorModal: props.setErrorModal
        };
        errorModal = <SecondaryProfilesErrorModal {...modalDataGroup} />;
        break;
      case 'upgradeIgProfile':
        errorModal = (
          <Modal
            primaryButtonAction={() => props.setErrorModal(null)}
            primaryButtonTitle={'OK'}
            size={'large'}
            closeOnClickOutside={false}
            onClose={() => props.setErrorModal(null)}
            title={'Direct publishing could not be enabled'}
            showCloseIcon={false}>
            <>
              <p>This Instagram Profile is either a Personal Profile, or is not connected to a Facebook Page.</p>
              <ul>
                <li>
                  In the Instagram mobile app, go to <strong>Settings {'>'} Accounts</strong> and if shown, click{' '}
                  <strong>Switch to Professional Account</strong> or <strong>Switch to Business Account.</strong>
                </li>
                <li>
                  Follow our <a onClick={() => beacon('article', '722', { type: 'modal' })}>Guide to connecting Instagram Profiles</a>
                  to connect your Instagram profile to your Facebook page.
                </li>
                <li>
                  Once done, click <strong>Enable Direct Publishing</strong> for this Profile again.
                </li>
              </ul>
            </>
          </Modal>
        );
        break;
      default:
        errorModal = null;
        break;
    }
  }

  if (props.infoModal && !props.errorModal) {
    infoModal = (
      <InfoModalView
        infoModal={props.infoModal}
        onClose={() => props.setInfoModal(null)}
        setInfoModal={props.setInfoModal}
      />
    );
  }

  if (props.secondaryProfilesModal && !props.errorModal) {
    secondaryProfileModal = (
      <ConnectSecondarySocialProfilesModal
        data={props.secondaryProfilesModal}
        onClose={() => {
          props.setSecondaryProfilesModal(null);
          socialOAuthVar(null);
        }}
      />
    );
  }

  if (props.directPublishingModal && !props.errorModal) {
    directPublishingModal = (
      <DirectPublishingModal
        onClose={() => {
          props.setDirectPublishingModal(null);
          socialOAuthVar(null);
        }}
        profile={props.directPublishingModal}
      />
    );
  }

  if (props.removeProfileModal.show && props.removeProfileModal.profileId && props.removeProfileModal.typename) {
    confirmDeleteProfileModal = (
      <DeleteModal removeProfileModal={props.removeProfileModal} setRemoveProfileModal={props.setRemoveProfileModal} />
    );
  }

  if (props.getProfilesData && props.getProfilesData.workspace.socialProfiles) {
    hasConnectedIgProfiles = !!props.getProfilesData.workspace.socialProfiles.filter(
      profile => profile.__typename === 'InstagramSocialProfile' && profile.connected
    )?.length;

    disconnectedProfiles = getFilteredProfiles(false, props.getProfilesData.workspace.socialProfiles);
    connectedProfiles = getFilteredProfiles(true, props.getProfilesData.workspace.socialProfiles);
  }

  return (
    <CenteredContentLayout loading={props.loading} headerTitle={'Social Profiles'} hasSidebar>
      {errorModal}
      {infoModal}
      {secondaryProfileModal}
      {confirmDeleteProfileModal}
      {directPublishingModal}
      <Panel>
        <div className={classes.SocialConnectorPanelContainer}>
          <h4 className={'margin-top-small-16 margin-bottom-small-12'}>Connect Profiles</h4>
          <div className={classes.SocialConnectorsGrid}>
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.INSTAGRAM}
              network={Network.INSTAGRAM}
              listItems={[
                {
                  label: 'Connect a Business/Creator Profile',
                  action: () => {
                    if (!canAddSocialProfile(1)) {
                      setLimitReachedModalState({
                        isOpen: true,
                        type: 'profiles'
                      });
                      return;
                    }
                    props.setShowIgConnectModal({ action: IgModalActionEnum.CONNECT });
                  }
                },
                {
                  label: 'Connect a Personal Profile (Coming soon...)',
                  action: () => {
                    if (!canAddSocialProfile(1)) {
                      setLimitReachedModalState({
                        isOpen: true,
                        type: 'profiles'
                      });
                      return;
                    }
                    props.setShowIgConnectModal(undefined);
                  },
                  disabled: true
                }
              ]}
            />
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.FACEBOOK}
              network={Network.FACEBOOK}
              action={() => {
                canAddSocialProfile(1)
                  ? profilesHelpers.setNetworkAndCallOAuth({
                      setLoading: props.setLoading,
                      oAuthProvider: SocialOAuthProvider.FACEBOOK,
                      profileNetwork: Network.FACEBOOK,
                      context: AuthContextEnum.connectPage,
                      startSocialOAuth: props.startSocialOAuth,
                      socialOAuthContext: SocialOAuthContext.CONNECT
                    })
                  : setLimitReachedModalState({
                      isOpen: true,
                      type: 'profiles'
                    });
              }}
            />
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.TWITTER}
              network={Network.TWITTER}
              action={() =>
                canAddSocialProfile(1)
                  ? profilesHelpers.setNetworkAndCallOAuth({
                      setLoading: props.setLoading,
                      oAuthProvider: SocialOAuthProvider.TWITTER,
                      profileNetwork: Network.TWITTER,
                      context: AuthContextEnum.connectProfile,
                      startSocialOAuth: props.startSocialOAuth,
                      socialOAuthContext: SocialOAuthContext.CONNECT
                    })
                  : setLimitReachedModalState({
                      isOpen: true,
                      type: 'profiles'
                    })
              }
            />
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.LINKEDIN}
              network={Network.LINKEDIN}
              listItems={props.connectLinkedinItems}
            />
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.GOOGLE}
              network={Network.GOOGLE}
              action={() =>
                canAddSocialProfile(1)
                  ? profilesHelpers.setNetworkAndCallOAuth({
                      setLoading: props.setLoading,
                      oAuthProvider: SocialOAuthProvider.GOOGLE,
                      profileNetwork: Network.GOOGLE,
                      context: AuthContextEnum.connectBusiness,
                      startSocialOAuth: props.startSocialOAuth,
                      socialOAuthContext: SocialOAuthContext.CONNECT
                    })
                  : setLimitReachedModalState({
                      isOpen: true,
                      type: 'profiles'
                    })
              }
            />
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.PINTEREST}
              network={Network.PINTEREST}
              action={() =>
                canAddSocialProfile(1)
                  ? profilesHelpers.setNetworkAndCallOAuth({
                      setLoading: props.setLoading,
                      oAuthProvider: SocialOAuthProvider.PINTEREST,
                      profileNetwork: Network.PINTEREST,
                      context: AuthContextEnum.connectProfile,
                      startSocialOAuth: props.startSocialOAuth,
                      socialOAuthContext: SocialOAuthContext.CONNECT
                    })
                  : setLimitReachedModalState({
                      isOpen: true,
                      type: 'profiles'
                    })
              }
            />
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.TIKTOK}
              network={Network.TIKTOK}
              action={() =>
                canAddSocialProfile(1)
                  ? profilesHelpers.setNetworkAndCallOAuth({
                      setLoading: props.setLoading,
                      oAuthProvider: SocialOAuthProvider.TIKTOK,
                      profileNetwork: Network.TIKTOK,
                      context: AuthContextEnum.connectProfile,
                      startSocialOAuth: props.startSocialOAuth,
                      socialOAuthContext: SocialOAuthContext.CONNECT
                    })
                  : setLimitReachedModalState({
                      isOpen: true,
                      type: 'profiles'
                    })
              }
            />
            <ConnectProfile network={Network.THREADS} />
            <ConnectProfile network={Network.YOUTUBE} />
            <ConnectProfile network={Network.GENERIC_PROFILE} />
          </div>
        </div>
      </Panel>
      {!!disconnectedProfiles.length && (
        <Panel>
          <PanelHeader
            title={'Disconnected Profiles'}
            tooltip={
              "Social profiles do occasionally get disconnected, for a variety of reasons: your access token has expired; you changed/revoked permissions on the platform; or the platform has some temporary bugs. Make sure you're logged into the platform as an admin of the profile that you're trying to re-connect."
            }
          />
          <div className={'margin-bottom-small-32'}>{disconnectedProfiles}</div>
        </Panel>
      )}
      <Panel>
        <PanelHeader title={'Connected Profiles'} />
        {hasConnectedIgProfiles && (
          <div className={'margin-bottom-small-16'}>
            <BoxNotification type={'info'}>
              <>
                <strong>Instagram publishing:</strong> Some posts cannot be published directly. Install our{' '}
                <Link to={'/settings/mobile'} target={'_blank'}>
                  mobile app
                </Link>{' '}
                to receive notifications when they are scheduled to be published.
                <a onClick={() => beacon('article', '721', { type: 'modal' })}>Learn more</a>
              </>
            </BoxNotification>
          </div>
        )}
        {!!connectedProfiles.length ? (
          <div className={'margin-bottom-small-32'}>{connectedProfiles}</div>
        ) : (
          <p className={'margin-bottom-small-32'}>You do not have any connected profiles.</p>
        )}
      </Panel>
      {props.showIgConnectModal && (
        <ConnectInstagramModal
          modalType={props.showIgConnectModal}
          setLoading={props.setLoading}
          startSocialOAuth={props.startSocialOAuth}
          closeHandler={() => props.setShowIgConnectModal(undefined)}
        />
      )}
    </CenteredContentLayout>
  );
};
